import { TimezoneNames } from '../models/timezones.model';
import { INVALID_TIMEZONE } from './format-date';

export const TIMEZONE_UNKNOWN = 'Timezone unknown';

// Reconcile Intl's coarse long-zone names with SonderMind's curated display vocabulary for the US
// zones the platform lists. Intl reports Pacific/Honolulu as "Hawaii-Aleutian"; the rest are
// identity mappings that anchor the curated names to the enum. Any zone whose coarse name is not
// here falls through to its raw Intl name.
const INTL_COARSE_TO_CURATED_NAME: { [intlCoarseName: string]: TimezoneNames; } = {
  Eastern: TimezoneNames.ET,
  Central: TimezoneNames.CT,
  Mountain: TimezoneNames.MT,
  Pacific: TimezoneNames.PT,
  Alaska: TimezoneNames.AL,
  'Hawaii-Aleutian': TimezoneNames.HI,
  Atlantic: TimezoneNames.AT,
};

const DST_SUFFIX = / (Standard|Daylight) Time$/;
const OFFSET_PREFIX = 'GMT';

function zoneNamePart(timeZone: string, date: Date, style: 'long' | 'short'): string {
  return (
    Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: style })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  );
}

/**
 * Derive a display label for an IANA time zone directly from Intl, so no per-zone lookup table is
 * needed. For the US zones SonderMind curates, the coarse Intl name is normalized onto the
 * TimezoneNames vocabulary ("Eastern (EST)", and Intl's "Hawaii-Aleutian" onto "Hawaii"); any other
 * valid zone renders its raw Intl long name and abbreviation ("Greenwich Mean Time (GMT)") -- a
 * usable label rather than "Timezone unknown". The 'en-US' locale is intentional (a US platform) so
 * the abbreviation is stable regardless of client locale. An offset-only zone (Etc/GMT+5) has no
 * spelled-out name and renders its GMT offset alone. Returns TIMEZONE_UNKNOWN for a falsy or
 * unresolvable zone -- the display-side counterpart to INVALID_TIMEZONE in format-date.ts, which
 * guards the input side.
 */
export function deriveTimezoneDisplayName(
  timeZone: string | undefined | null,
  now: Date = new Date(),
): string {
  if (!timeZone || timeZone === INVALID_TIMEZONE) {
    return TIMEZONE_UNKNOWN;
  }

  try {
    const coarseName = zoneNamePart(timeZone, now, 'long').replace(DST_SUFFIX, '');
    const abbrev = zoneNamePart(timeZone, now, 'short');

    // An offset-only zone (Etc/GMT+5) has no spelled-out name -- Intl returns a GMT offset for the
    // long name too, so render the short offset alone rather than doubling it. Keyed on the long
    // name so a genuinely-named zone whose abbreviation is GMT-shaped (Europe/London -> "GMT") is
    // unaffected: Intl's long style for such zones is spelled out ("Greenwich Mean Time").
    if (coarseName.startsWith(OFFSET_PREFIX)) {
      return abbrev || TIMEZONE_UNKNOWN;
    }

    if (!coarseName || !abbrev) {
      return TIMEZONE_UNKNOWN;
    }

    const displayName = INTL_COARSE_TO_CURATED_NAME[coarseName] ?? coarseName;
    return `${displayName} (${abbrev})`;
  } catch {
    return TIMEZONE_UNKNOWN;
  }
}
